import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Mail, Trash2, Paperclip, User, Rocket, Check, Save, Trophy, ChevronLeft, ChevronRight, Minus, Plus, PartyPopper } from "lucide-react";
import { AdminComposer } from "@/components/site/AdminComposer";
import { AdminHeader, AdminGate } from "@/pages/AdminATTPage";
import { ATT_BLUE } from "@/attData";

const API = process.env.REACT_APP_BACKEND_URL;
const REP_STATUSES = ["New", "Interview", "Onboarding", "Hired", "Declined"];
const statusColor = { New: "#00A8E0", Interview: "#d4af37", Onboarding: "#a78bfa", Hired: "#34d399", Declined: "#f87171" };

const REP_STEPS = [
  ["reviewed", "Application reviewed"],
  ["interview", "Interview completed"],
  ["background", "Background check passed"],
  ["drugtest", "Drug test passed"],
  ["agreement", "Contractor agreement signed"],
  ["w9", "W-9 / payment info collected"],
  ["badge", "Badge approved & ordered"],
  ["shirt", "Shirt & gear ordered"],
  ["training", "Training completed"],
  ["active", "First day scheduled — ACTIVE"],
];

export const AGENCY_STEPS = [
  ["reviewed", "Application reviewed"],
  ["interview", "Interview completed"],
  ["background", "Background check passed"],
  ["course", "Licensing course enrolled"],
  ["exam", "State exam passed"],
  ["contracting", "Carrier contracting completed"],
  ["training", "Training completed"],
  ["active", "First appointment set — ACTIVE"],
];

export const OnboardingPanel = ({ item, stepDefs, onSave, onWelcome, accent }) => {
  const ob = item.onboarding;
  const [notes, setNotes] = useState(ob?.notes || "");
  if (!ob) {
    return (
      <button
        onClick={() => onSave(item.id, { steps: {}, notes: "", started_at: new Date().toISOString().slice(0, 10) })}
        data-testid={`onboard-start-${item.id}`}
        className="inline-flex items-center gap-2 rounded-full border px-5 py-2 font-body text-xs tracking-widest uppercase font-semibold transition-colors hover:bg-white/5"
        style={{ borderColor: `${accent}66`, color: accent }}
      >
        <Rocket size={13} /> Start Onboarding
      </button>
    );
  }
  const done = stepDefs.filter(([id]) => ob.steps?.[id]).length;
  const pct = Math.round((done / stepDefs.length) * 100);
  return (
    <div className="mt-2 w-full rounded-2xl border border-white/10 bg-black/30 p-5" data-testid={`onboard-panel-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2">
          <Rocket size={13} style={{ color: accent }} /> Onboarding — <span className="font-bold" style={{ color: accent }}>{done}/{stepDefs.length} complete ({pct}%)</span>
        </p>
        <div className="flex items-center gap-3">
          {ob.started_at && <span className="font-body text-[11px] text-white/35">Started {ob.started_at}</span>}
          {onWelcome && (
            <button
              type="button"
              data-testid={`onboard-welcome-${item.id}`}
              onClick={onWelcome}
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-body text-[11px] tracking-widest uppercase font-semibold text-black bg-emerald-400 hover:bg-emerald-300 transition-colors"
            >
              <PartyPopper size={12} /> Send Welcome Email
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? "#34d399" : accent }} data-testid={`onboard-progress-${item.id}`} />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {stepDefs.map(([id, label]) => {
          const checked = !!ob.steps?.[id];
          return (
            <button
              key={id}
              type="button"
              data-testid={`onboard-step-${id}-${item.id}`}
              onClick={() => onSave(item.id, { ...ob, notes, steps: { ...ob.steps, [id]: !checked } })}
              className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 font-body text-sm text-left transition-colors duration-300 ${checked ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-white/60 hover:border-white/30"}`}
            >
              <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${checked ? "border-emerald-400 bg-emerald-400 text-black" : "border-white/30"}`}>
                {checked && <Check size={13} strokeWidth={3} />}
              </span>
              {label}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex gap-2">
        <input
          data-testid={`onboard-notes-${item.id}`}
          className="flex-1 rounded-xl border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
          placeholder="Onboarding notes (start date, gear sizes, schedule...)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <button
          type="button"
          data-testid={`onboard-save-notes-${item.id}`}
          onClick={() => onSave(item.id, { ...ob, notes })}
          className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 font-body text-xs tracking-widest uppercase text-white font-semibold"
          style={{ backgroundColor: accent }}
        >
          <Save size={13} /> Save
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/35">{label}</p>
    <p className="font-body text-sm text-white/85 break-words">{value || "—"}</p>
  </div>
);

const YesNoChip = ({ label, value, badIfYes = false }) => {
  const isYes = (value || "").toLowerCase() === "yes";
  const bad = badIfYes ? isYes : !isYes && value;
  return (
    <span className={`rounded-full px-3 py-1 font-body text-[11px] tracking-wide ${!value ? "border border-white/10 text-white/40" : bad ? "bg-red-500/15 border border-red-500/40 text-red-300" : "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"}`}>
      {label}: {value || "—"}
    </span>
  );
};

const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (m) => new Date(`${m}-02T12:00:00`).toLocaleString("en-US", { month: "long", year: "numeric" });

export const welcomeMessage = (firstName, position, ob) => {
  let msg = `Welcome to the Heritage Family Protection Agency team! We're excited to officially onboard you as our newest ${position}.

Here's what happens next:
• Start date: ${ob?.started_at || "we'll confirm your first day with you shortly"}`;
  if (ob?.notes) msg += `\n• From our team: ${ob.notes}`;
  msg += `
• Finish any remaining onboarding steps (paperwork, background check, training)
• Your badge and shirt will be ordered once your photo and size are confirmed

If you have any questions before your first day, call us at (470) 814-0464.

Welcome aboard — let's get to work!`;
  return msg;
};

export default function AdminApplicantsPage() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("reps");
  const [reps, setReps] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [sales, setSales] = useState({});

  const loadSales = async (m, accessCode = code) => {
    const res = await fetch(`${API}/api/att/rep-sales?month=${m}`, { headers: { "X-Admin-Code": accessCode } });
    if (res.ok) {
      const data = await res.json();
      setSales(Object.fromEntries(data.map((s) => [s.rep_id, s.count])));
    }
  };

  const changeMonth = (delta) => {
    const m = shiftMonth(month, delta);
    setMonth(m);
    loadSales(m);
  };

  const setSale = async (repId, count) => {
    const c = Math.max(0, count);
    setSales((s) => ({ ...s, [repId]: c }));
    const res = await fetch(`${API}/api/att/rep-sales/${repId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify({ month, count: c }),
    });
    if (!res.ok) toast.error("Couldn't save sales count.");
  };

  const load = async (e, accessCode = code) => {
    e && e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Code": accessCode };
      const rRes = await fetch(`${API}/api/att/rep-applications`, { headers });
      if (rRes.status === 401) { setError("Invalid access code."); setAuthed(false); return; }
      if (!rRes.ok) throw new Error();
      setReps(await rRes.json());
      setAuthed(true);
      loadSales(month, accessCode);
    } catch {
      setError("Could not load data. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateRepStatus = async (id, status) => {
    const prev = reps.find((x) => x.id === id)?.status;
    setReps((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    const res = await fetch(`${API}/api/att/rep-applications/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify({ status }),
    });
    if (res.ok) toast.success(`Applicant marked ${status}`);
    else {
      setReps((r) => r.map((x) => (x.id === id ? { ...x, status: prev } : x)));
      toast.error("Couldn't update status.");
    }
  };

  const removeRep = async (id) => {
    if (!window.confirm("Delete this AT&T rep application permanently?")) return;
    const res = await fetch(`${API}/api/att/rep-applications/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) { setReps((r) => r.filter((x) => x.id !== id)); toast.success("Application deleted."); }
    else toast.error("Couldn't delete. Try again.");
  };

  const saveRepOnboarding = async (id, onboarding) => {
    setReps((r) => r.map((x) => (x.id === id ? { ...x, onboarding } : x)));
    const res = await fetch(`${API}/api/att/rep-applications/${id}/onboarding`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify(onboarding),
    });
    if (res.ok) toast.success("Onboarding saved.");
    else toast.error("Couldn't save onboarding.");
  };

  return (
    <div className="bg-[#081226] min-h-screen" data-testid="admin-applicants-page">
      <AdminHeader title="AT&T Applicants" subtitle="back office." />
      <section className="relative py-10 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {!authed ? (
            <AdminGate code={code} setCode={setCode} onSubmit={load} error={error} loading={loading} />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setTab("reps")} data-testid="applicants-tab-reps" className={`rounded-full px-5 py-2.5 font-body text-sm tracking-wide transition-colors ${tab === "reps" ? "text-white font-semibold" : "border border-white/15 text-white/60 hover:text-white"}`} style={tab === "reps" ? { backgroundColor: ATT_BLUE } : {}}>
                    AT&T Sales Reps ({reps.length})
                  </button>
                  <button onClick={() => { setTab("leaderboard"); loadSales(month); }} data-testid="applicants-tab-leaderboard" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm tracking-wide transition-colors ${tab === "leaderboard" ? "bg-emerald-400 text-black font-semibold" : "border border-white/15 text-white/60 hover:text-white"}`}>
                    <Trophy size={14} /> Sales Leaderboard
                  </button>
                </div>
                <button onClick={() => load()} data-testid="applicants-refresh" className="inline-flex items-center gap-2 font-body text-sm hover:opacity-80 transition-opacity" style={{ color: ATT_BLUE }}>
                  <RefreshCw size={15} /> Refresh
                </button>
              </div>

              {tab === "reps" && (
                reps.length === 0 ? <p className="font-body text-white/50" data-testid="applicants-no-reps">No AT&T rep applications yet.</p> : (
                  <div className="space-y-4">
                    {reps.map((a) => (
                      <div key={a.id} data-testid={`rep-admin-${a.id}`} className="rounded-2xl border border-white/10 bg-[#0a192f] p-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <h3 className="font-serif-display text-2xl text-white flex items-center gap-3">
                            <User size={20} style={{ color: ATT_BLUE }} />
                            {a.first_name} {a.last_name}
                          </h3>
                          <div className="flex items-center gap-3">
                            <span className="font-body text-xs text-white/40">{new Date(a.created_at).toLocaleString()}</span>
                            <select
                              data-testid={`rep-admin-status-${a.id}`}
                              value={a.status || "New"}
                              onChange={(e) => updateRepStatus(a.id, e.target.value)}
                              className="rounded-full border border-white/20 bg-black/50 px-3 py-1.5 font-body text-xs font-bold uppercase tracking-wider focus:outline-none"
                              style={{ color: statusColor[a.status || "New"] }}
                            >
                              {REP_STATUSES.map((s) => <option key={s} value={s} className="bg-[#0a192f] text-white">{s}</option>)}
                            </select>
                          </div>
                        </div>
                        <p className="mt-1 font-body text-sm font-semibold" style={{ color: ATT_BLUE }}>{a.position || "AT&T Sales Rep"}</p>

                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
                          <Field label="Email" value={<a href={`mailto:${a.email}`} className="hover:text-[#00A8E0]">{a.email}</a>} />
                          <Field label="Phone" value={<a href={`tel:${a.phone}`} className="hover:text-[#00A8E0]">{a.phone}</a>} />
                          <Field label="Address" value={[a.address, a.city, a.zip_code].filter(Boolean).join(", ")} />
                          <Field label="Gender" value={a.gender} />
                          <Field label="Shirt size" value={a.shirt_size} />
                          <Field label="Signed" value={a.signature} />
                          <Field label="Date" value={a.signature_date} />
                          <Field label="Applicant ID" value={a.id.slice(0, 8)} />
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <YesNoChip label="Background check" value={a.background_check} />
                          <YesNoChip label="Drug test" value={a.drug_test} />
                          <YesNoChip label="Convicted" value={a.convicted} badIfYes />
                        </div>
                        {a.convicted_explain && <p className="mt-2 font-body text-sm text-white/55 italic">Conviction details: "{a.convicted_explain}"</p>}

                        <div className="mt-5 flex flex-wrap gap-3">
                          {a.badge_path && (
                            <a
                              href={`${API}/api/att/rep-badge/${a.badge_path}?code=${encodeURIComponent(code)}`}
                              data-testid={`rep-admin-badge-${a.id}`}
                              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 font-body text-xs tracking-widest uppercase text-black font-semibold"
                            >
                              <Paperclip size={13} /> Download Badge Photo
                            </a>
                          )}
                          <button
                            onClick={() => setCompose({ to_email: a.email, to_name: `${a.first_name} ${a.last_name}`, subject: `Your AT&T Sales Rep application — ${a.position || "Heritage"}`, message: "" })}
                            data-testid={`rep-admin-email-${a.id}`}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-body text-xs tracking-widest uppercase text-white font-semibold"
                            style={{ backgroundColor: ATT_BLUE }}
                          >
                            <Mail size={13} /> Email Applicant
                          </button>
                          <button
                            onClick={() => removeRep(a.id)}
                            data-testid={`rep-admin-delete-${a.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                          <OnboardingPanel
                            item={a}
                            stepDefs={REP_STEPS}
                            onSave={saveRepOnboarding}
                            accent={ATT_BLUE}
                            onWelcome={() => setCompose({ to_email: a.email, to_name: `${a.first_name} ${a.last_name}`, subject: `Welcome to the team, ${a.first_name}! — ${a.position || "AT&T Sales Rep"}`, message: welcomeMessage(a.first_name, a.position || "AT&T Sales Rep", a.onboarding) })}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === "leaderboard" && (() => {
                const hired = reps.filter((r) => r.status === "Hired").sort((a, b) => (sales[b.id] || 0) - (sales[a.id] || 0));
                const total = hired.reduce((sum, r) => sum + (sales[r.id] || 0), 0);
                const rankStyle = ["#d4af37", "#c0c0c0", "#cd7f32"];
                return (
                  <div data-testid="leaderboard-panel">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                      <div className="flex items-center gap-3">
                        <button onClick={() => changeMonth(-1)} data-testid="leaderboard-prev-month" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/60 hover:border-emerald-400 hover:text-emerald-300 transition-colors">
                          <ChevronLeft size={16} />
                        </button>
                        <p className="font-serif-display text-2xl text-white min-w-[180px] text-center" data-testid="leaderboard-month">{monthLabel(month)}</p>
                        <button onClick={() => changeMonth(1)} data-testid="leaderboard-next-month" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/60 hover:border-emerald-400 hover:text-emerald-300 transition-colors">
                          <ChevronRight size={16} />
                        </button>
                      </div>
                      <p className="font-body text-sm text-white/50">Team total: <span className="font-bold text-emerald-300">{total} sales</span></p>
                    </div>
                    {hired.length === 0 ? (
                      <p className="font-body text-white/50" data-testid="leaderboard-empty">
                        No hired reps yet — set an applicant's status to <span className="text-emerald-300 font-semibold">Hired</span> on the AT&T Sales Reps tab and they'll show up here.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {hired.map((r, i) => (
                          <div key={r.id} data-testid={`leaderboard-row-${r.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#0a192f] px-6 py-4">
                            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-body text-sm font-bold" style={{ backgroundColor: i < 3 ? rankStyle[i] : "rgba(255,255,255,0.08)", color: i < 3 ? "#111" : "rgba(255,255,255,0.6)" }}>
                              {i < 3 ? <Trophy size={16} /> : `#${i + 1}`}
                            </span>
                            <div className="flex-1 min-w-[160px]">
                              <p className="font-serif-display text-xl text-white">{r.first_name} {r.last_name}</p>
                              <p className="font-body text-xs" style={{ color: ATT_BLUE }}>{r.position || "AT&T Sales Rep"}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <button onClick={() => setSale(r.id, (sales[r.id] || 0) - 1)} data-testid={`leaderboard-minus-${r.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-red-400 hover:text-red-300 transition-colors">
                                <Minus size={15} />
                              </button>
                              <div className="text-center min-w-[70px]">
                                <p className="font-serif-display text-3xl font-bold text-emerald-300" data-testid={`leaderboard-count-${r.id}`}>{sales[r.id] || 0}</p>
                                <p className="font-body text-[10px] uppercase tracking-widest text-white/35">sales</p>
                              </div>
                              <button onClick={() => setSale(r.id, (sales[r.id] || 0) + 1)} data-testid={`leaderboard-plus-${r.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-emerald-400 hover:text-emerald-300 transition-colors">
                                <Plus size={15} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {compose && <AdminComposer open onClose={() => setCompose(null)} code={code} initial={compose} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
